import { OptionItem } from '@hooks/basket/useUpdateBasket';
import { gql } from 'graphql-request';
import useSWRMutation from 'swr/mutation';
import { captureMessage } from '@sentry/nextjs';
import { productAdded } from '@repo/ui/src/services/analytics';
import { trackCart } from '@functions/monetate/cart';
import { BasketFragment } from '@graphql/fragments';
import { basketRequest, OutOfStockError } from '@hooks/basket/_utils';
import { formatBasket, useBasketMutation } from '@hooks/basket/useBasket';
import { useCustomerIdToken } from '@hooks/customer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID } from '@repo/ui/src/modules/UI';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import { basketLineInputToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { GraphQLRequestWithTokenProps } from '@repo/ui/src/utils/graphql-request';

export interface BasketItemInput {
  itemId: string;
  mainImageUrl?: string;
  productId: string;
  quantity: number;
  warrantyPlanId?: string;
  category?: string;
  description?: string;
}

interface Key extends GraphQLRequestWithTokenProps {
  locale: string;
  currency: string;
}

interface ArgValue {
  sku: string;
  quantity: number;
  mainImageUrl?: string;
  mainCategory?: string;
  shortDescription?: string;
  description?: string;
  warrantyPlanId?: string;
  optionItems?: Array<OptionItem>;
}

const fetcher = async (
  { currency, ...props }: Key,
  { arg: products }: { arg: Array<ArgValue> },
) => {
  const items: BasketItemInput[] = products.map((product) => ({
    itemId: product.sku,
    mainImageUrl: product.mainImageUrl,
    productId: product.sku,
    quantity: product.quantity,
    category: product.mainCategory,
    description: product.shortDescription || product.description,
    warrantyPlanId: product.warrantyPlanId,
    optionItems: product.optionItems?.map((option) => ({
      id: option.optionId,
      valueId: option.optionValueId,
    })),
  }));

  const { addItemsToBasket: basket } = await basketRequest({
    ...props,
    variables: { items, currency },
  });

  if (basket?.outOfStockItems?.length) {
    throw new OutOfStockError(
      products.find((product) => basket.outOfStockItems[0].sku === product.sku),
    );
  }

  if (basket) {
    trackCart(basket);

    productAdded({
      // todo do we need to account for warranties?
      products: (products as any).map(basketLineInputToAnalytics),
      basket_token: basket.basketJWT,
      affiliation: 'website',
      brand: 'Traeger',
      cart_id: basket.id,
      currency: basket.currency,
      locale: props.locale,
    });
  }

  return basket;
};

export const addItemsToBasket = gql`
  ${BasketFragment}
  mutation AddBasket($items: [BasketItemInput!]!, $currency: String) {
    addItemsToBasket(siteId: "", items: $items, currency: $currency) {
      ...Basket
    }
  }
`;

const useAddToBasket = () => {
  const { locale, currency } = useCurrentUrl();
  const idToken = useCustomerIdToken();
  const mutate = useBasketMutation();
  const { pushModal } = useUI();

  return useSWRMutation({ document: addItemsToBasket, idToken, locale, currency }, fetcher, {
    async onSuccess(basket) {
      const formattedBasket = formatBasket(basket, locale);
      await mutate(formattedBasket, { revalidate: false });
      pushModal(ModalID.cart);
    },

    async onError(error: Error) {
      if (error instanceof OutOfStockError && error.info) {
        pushModal(ModalID.outOfStock, error.info);
      } else {
        // @ts-expect-error response definitely exists
        const basketData = error.response?.data?.data?.addItemsToBasket;
        if (basketData) {
          const formattedBasket = formatBasket(basketData, locale);
          await mutate(formattedBasket, { revalidate: false });
        }
        captureMessage('Add to Cart Error', {
          level: 'error',
          extra: {
            message: error.message,
          },
        });
        pushModal(ModalID.error);
      }
    },
    throwOnError: false,
  });
};

export default useAddToBasket;
